import React, { useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import "../../styles/blog-listing.css";
import { categories, posts, formatDate, Cover } from "../../data/blogData";

const PER_PAGE = 9;

const Icon = {
  calendar: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3.5" y="5" width="17" height="15" rx="3" />
      <path d="M8 3v4M16 3v4M3.5 10h17" />
    </svg>
  ),
  clock: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  arrow: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  ),
  left: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M19 12H5M11 6l-6 6 6 6" />
    </svg>
  ),
  crumb: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  ),
  search: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-4-4" />
    </svg>
  ),
  empty: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-4-4M8.5 11h5" />
    </svg>
  ),
};

function pageList(total, cur) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out = [1];
  if (cur > 3) out.push("…");
  for (let n = Math.max(2, cur - 1); n <= Math.min(total - 1, cur + 1); n++)
    out.push(n);
  if (cur < total - 2) out.push("…");
  out.push(total);
  return out;
}

export default function BlogList() {
  // topic, search text and page live in the URL: /blogs?topic=Networking&q=wifi&page=2
  const [params, setParams] = useSearchParams();
  const cat = categories.includes(params.get("topic"))
    ? params.get("topic")
    : "All";
  const q = params.get("q") || "";
  const requested = parseInt(params.get("page"), 10) || 1;

  const [animate, setAnimate] = useState(false); // fade cards in only after the visitor interacts
  const barRef = useRef(null);

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    return posts.filter(
      (p) =>
        (cat === "All" || p.tag === cat) &&
        (!term || `${p.title} ${p.text} ${p.tag}`.toLowerCase().includes(term)),
    );
  }, [cat, q]);

  const totalPages = Math.max(1, Math.ceil(list.length / PER_PAGE));
  const page = Math.min(Math.max(requested, 1), totalPages);
  const start = (page - 1) * PER_PAGE;
  const visible = list.slice(start, start + PER_PAGE);

  const update = (patch, scroll = false) => {
    const next = { topic: cat, q, page, ...patch };
    const sp = new URLSearchParams();
    if (next.topic !== "All") sp.set("topic", next.topic);
    if (next.q.trim()) sp.set("q", next.q);
    if (next.page > 1) sp.set("page", String(next.page));
    setAnimate(true);
    setParams(sp, { replace: true });
    if (scroll)
      barRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="gt9-lst">
      {/* shared gradients for the illustrated covers */}
      <svg
        width="0"
        height="0"
        style={{ position: "absolute" }}
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <linearGradient id="gt9-bg" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#0b1a3a" />
            <stop offset="1" stopColor="#14264f" />
          </linearGradient>
          <radialGradient id="gt9-glow" cx="0.82" cy="0.1" r="0.7">
            <stop offset="0" stopColor="#d2a566" stopOpacity=".38" />
            <stop offset="1" stopColor="#d2a566" stopOpacity="0" />
          </radialGradient>
          <pattern
            id="gt9-gridpat"
            width="28"
            height="28"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M28 0H0V28"
              fill="none"
              stroke="#fff"
              strokeOpacity=".05"
            />
          </pattern>
        </defs>
      </svg>

      {/* Page hero */}
      <section className="gt9-lst__hero">
        <div className="gt9-container">
          <ol className="gt9-crumbs" aria-label="Breadcrumb">
            <li>
              <Link to="/">Home</Link>
              {Icon.crumb}
            </li>
            <li aria-current="page">Blog &amp; news</li>
          </ol>
          <span className="gt9-lst__eyebrow">Blog &amp; news</span>
          <h1 className="gt9-lst__title">
            Insights from our <span className="hl">tech team</span>
          </h1>
          <p className="gt9-lst__lead">
            Practical guides on cloud, security, networking and IT strategy,
            written by the people who run it every day.
          </p>
        </div>
      </section>

      <section className="gt9-lst__body" aria-label="Articles">
        <div className="gt9-container">
          {/* Search + topic filters */}
          <div className="gt9-bar2" ref={barRef}>
            <label className="gt9-search">
              {Icon.search}
              <input
                type="search"
                value={q}
                onChange={(e) => update({ q: e.target.value, page: 1 })}
                placeholder="Search articles"
                aria-label="Search articles"
                autoComplete="off"
              />
            </label>
            <div
              className="gt9-chips"
              role="group"
              aria-label="Filter by topic"
            >
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`gt9-chip${c === cat ? " is-active" : ""}`}
                  aria-pressed={c === cat}
                  onClick={() => update({ topic: c, page: 1 })}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="gt9-meta">
            <p aria-live="polite" style={{ margin: 0 }}>
              {list.length === 0 ? (
                "No articles match your search"
              ) : (
                <>
                  Showing{" "}
                  <b>
                    {start + 1}–{start + visible.length}
                  </b>{" "}
                  of <b>{list.length}</b> articles
                  {cat !== "All" && (
                    <>
                      {" "}
                      in <b>{cat}</b>
                    </>
                  )}
                </>
              )}
            </p>
          </div>

          {/* 3 x 3 grid */}
          <div className="gt9-grid3">
            {list.length === 0 ? (
              <div className="gt9-empty">
                {Icon.empty}
                <h3>No articles found</h3>
                <p>Try a different keyword or pick another topic.</p>
                <button
                  type="button"
                  onClick={() => update({ topic: "All", q: "", page: 1 })}
                >
                  Clear filters
                </button>
              </div>
            ) : (
              visible.map((p, i) => (
                <article
                  key={`${p.slug}-${page}-${cat}`}
                  className={`gt9-card${animate ? " gt9-anim" : ""}`}
                  style={{ "--i": i }}
                >
                  <Link
                    className="gt9-card__media"
                    to={`/blogs/${p.slug}`}
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    {p.image ? (
                      <img src={p.image} alt="" loading="lazy" />
                    ) : (
                      <Cover type={p.cover} />
                    )}
                    <span className="gt9-card__tag">{p.tag}</span>
                  </Link>
                  <div className="gt9-card__body">
                    <div className="gt9-card__meta">
                      <span>
                        {Icon.calendar}
                        <time dateTime={p.iso}>{formatDate(p.iso)}</time>
                      </span>
                      <span>
                        {Icon.clock}
                        {p.read} min read
                      </span>
                    </div>
                    <h3 className="gt9-card__title">
                      <Link to={`/blogs/${p.slug}`}>{p.title}</Link>
                    </h3>
                    <p className="gt9-card__text">{p.text}</p>
                    <Link className="gt9-card__more" to={`/blogs/${p.slug}`}>
                      Read more {Icon.arrow}
                    </Link>
                  </div>
                </article>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="gt9-pg" aria-label="Pagination">
              <div className="gt9-pg__row">
                <button
                  type="button"
                  className="gt9-pg__btn gt9-pg__nav"
                  disabled={page === 1}
                  aria-label="Previous page"
                  onClick={() => update({ page: page - 1 }, true)}
                >
                  {Icon.left}
                  <span>Previous</span>
                </button>
                <ul>
                  {pageList(totalPages, page).map((n, i) =>
                    n === "…" ? (
                      <li
                        key={`dots-${i}`}
                        className="gt9-pg__dots"
                        aria-hidden="true"
                      >
                        …
                      </li>
                    ) : (
                      <li key={n}>
                        <button
                          type="button"
                          className={`gt9-pg__btn${n === page ? " is-current" : ""}`}
                          aria-label={`Page ${n}`}
                          aria-current={n === page ? "page" : undefined}
                          onClick={() =>
                            n !== page && update({ page: n }, true)
                          }
                        >
                          {n}
                        </button>
                      </li>
                    ),
                  )}
                </ul>
                <button
                  type="button"
                  className="gt9-pg__btn gt9-pg__nav"
                  disabled={page === totalPages}
                  aria-label="Next page"
                  onClick={() => update({ page: page + 1 }, true)}
                >
                  <span>Next</span>
                  {Icon.arrow}
                </button>
              </div>
              <p className="gt9-pg__info">
                Page {page} of {totalPages}
              </p>
            </nav>
          )}
        </div>
      </section>
    </main>
  );
}
